import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import ComputerIcon from '@mui/icons-material/Computer';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReportIcon from '@mui/icons-material/Report';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import {
  getSecurityComplianceDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import type { WazuhComplianceControlItem } from 'src/types/mindtime/wazuhCompliance';
import type { Column, TableData } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  edrMonoStyle,
  edrMutedStyle,
  EdrPageHeader,
  EdrStatusPill,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import { getComplianceControlId, complianceStatusToTone, toComplianceTitle } from './complianceDetailModel';
import { COMPLIANCE_CATEGORY_FILTER_OPTIONS, COMPLIANCE_FRAMEWORK_FILTER_OPTIONS } from './complianceFrameworkOptions';
import { useComplianceData, useComplianceExport, type ComplianceSortParams } from './useComplianceData';

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const TITLE_MAX_LEN = 60;

const TruncatedTitle: FC<{ text?: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const value = text?.trim() ?? '';
  if (!value) return <span style={edrMutedStyle}>—</span>;

  const canExpand = value.length > TITLE_MAX_LEN;
  const shown = canExpand && !expanded ? `${value.slice(0, TITLE_MAX_LEN)}…` : value;

  if (!canExpand) {
    return (
      <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>{shown}</span>
    );
  }

  return (
    <button
      type="button"
      title={expanded ? undefined : value}
      aria-expanded={expanded}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded((v) => !v);
      }}
      style={{
        fontWeight: 600,
        color: EDR_COLORS.textPrimary,
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        maxWidth: expanded ? undefined : 280,
        whiteSpace: expanded ? 'normal' : 'nowrap',
        wordBreak: expanded ? 'break-word' : undefined,
      }}
    >
      {shown}
    </button>
  );
};

const FrameworkTag: FC<{ value: string }> = ({ value }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 600,
      background: EDR_COLORS.brand50,
      color: EDR_COLORS.brand600,
      border: `1px solid ${EDR_COLORS.brand200}`,
      whiteSpace: 'nowrap',
    }}
  >
    {value}
  </span>
);

const FrameworkTags: FC<{ values?: string[] }> = ({ values }) => {
  const items = (values ?? []).filter(Boolean);
  if (!items.length) return <span style={edrMutedStyle}>—</span>;
  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: 4, maxWidth: 180 }}>
      {items.map((value) => (
        <FrameworkTag key={value} value={value} />
      ))}
    </Box>
  );
};

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Configuration Assessment', to: getSecurityNavItemPath('configuration-assessment') ?? '' },
  ],
};

const STATUS_OPTIONS = [
  { label: 'Pass', value: 'pass' },
  { label: 'Fail', value: 'fail' },
  { label: 'Needs Review', value: 'needs_review' },
];

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const ConfigurationAssessment: FC = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.configurationAssessment);
  const {
    framework = '',
    status = '',
    category = '',
    wazuhAgentId = '',
    organizationId = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortParams, setSortParams] = useState<ComplianceSortParams | null>(null);
  const [resetSortFlag, setResetSortFlag] = useState(false);

  const complianceFilters = useMemo(
    () => ({
      framework,
      status,
      category,
      wazuhAgentId,
      organizationId,
    }),
    [framework, status, category, wazuhAgentId, organizationId]
  );

  const { loading, summary, controls } = useComplianceData(
    page,
    rowsPerPage,
    search,
    complianceFilters,
    sortParams
  );
  const { exporting, handleExport } = useComplianceExport(search, complianceFilters, sortParams);

  const items = controls?.items ?? [];
  const totalRecords = controls?.totalRecords ?? 0;

  const openComplianceDetail = useCallback(
    (item: WazuhComplianceControlItem) => {
      const id = getComplianceControlId(item);
      if (!id) return;
      navigate(getSecurityComplianceDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const toolbarFilters = useMemo((): EdrToolbarFilter[] => [
    {
      id: 'framework',
      label: 'Framework',
      icon: <AccountTreeIcon style={{ fontSize: 14 }} />,
      options: COMPLIANCE_FRAMEWORK_FILTER_OPTIONS,
      value: framework,
      onChange: (value) => {
        setFilter('framework', value);
        setPage(0);
      },
    },
    {
      id: 'status',
      label: 'Status',
      icon: <TrendingUpIcon style={{ fontSize: 14 }} />,
      options: STATUS_OPTIONS,
      value: status,
      onChange: (value) => {
        setFilter('status', value);
        setPage(0);
      },
    },
    {
      id: 'category',
      label: 'Category',
      icon: <CategoryIcon style={{ fontSize: 14 }} />,
      options: COMPLIANCE_CATEGORY_FILTER_OPTIONS,
      value: category,
      onChange: (value) => {
        setFilter('category', value);
        setPage(0);
      },
    },
    {
      id: 'endpoint',
      label: 'Endpoint',
      variant: 'searchableEndpoint' as const,
      pageOrganizationId: organizationId || undefined,
      value: wazuhAgentId,
      onChange: (value: string) => {
        setFilter('wazuhAgentId', value);
        setPage(0);
      },
    },
    ...(isReseller
      ? [
        {
          id: 'org',
          label: 'Organization',
          variant: 'resellerClient' as const,
          value: organizationId,
          onChange: (value: string) => {
            setFilter('organizationId', value);
            setFilter('wazuhAgentId', '');
            setPage(0);
          },
        },
      ]
      : []),

    {
      id: 'date',
      label: 'Date Range',
      icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
      options: DATE_RANGE_OPTIONS,
      value: dateRange,
      onChange: (value) => {
        setDateRange(value as WazuhDateRangeOption);
        setPage(0);
      },
    },
  ],
    [
      framework,
      status,
      category,
      wazuhAgentId,
      organizationId,
      dateRange,
      isReseller,
      setFilter,
      setDateRange,
    ]
  );

  const handleResetFiltersBase = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);
  const handleResetFilters = () => {
    handleResetFiltersBase();
    setSortParams(null);
    setResetSortFlag((prev) => !prev);
  };

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Security Check',
        sortable: true,
        render: (row) => (
          <TruncatedTitle text={(row as unknown as WazuhComplianceControlItem).title} />
        ),
      },
      {
        id: 'framework',
        label: 'Framework',
        sortable: true,
        render: (row) => (
          <FrameworkTags values={(row as unknown as WazuhComplianceControlItem).framework} />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => (
          <EdrStatusPill
            tone={complianceStatusToTone((row as unknown as WazuhComplianceControlItem).status)}
            label={toComplianceTitle((row as unknown as WazuhComplianceControlItem).status)}
          />
        ),
      },
      {
        id: 'category',
        label: 'Category',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhComplianceControlItem).category}
          </span>
        ),
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        sortable: true,
        render: (row) => (
          <span style={edrMonoStyle}>
            {(row as unknown as WazuhComplianceControlItem).endpoint}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhComplianceControlItem).organization}
          </span>
        ),
      },

      {
        id: 'lastChecked',
        label: 'Last Checked',
        sortable: true,
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhComplianceControlItem).lastChecked} />
        ),
      },
    ],
    []
  );

  const complianceScore = summary?.overallComplianceScore ?? 0;
  const scoreAccent = framework ? EDR_COLORS.brand600 : EDR_COLORS.success;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Configuration Compliance')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Configuration Compliance"
          subtitle="Monitor security configuration compliance across all managed endpoints."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        />

        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Overall Compliance Score"
            value={`${formatNumber(summary?.overallComplianceScore)}%`}
            icon={<TrendingUpIcon style={{ fontSize: 18 }} />}
            iconColor={scoreAccent}
            accent={scoreAccent}
          >
            <Box width="100%" mt={0.5}>
              <LinearProgress
                variant="determinate"
                value={complianceScore}
                style={{ height: 4, borderRadius: 2, background: EDR_COLORS.slate200 }}
              />
            </Box>
          </EdrKpiCard>
          <EdrKpiCard
            label="Compliant Endpoints"
            value={formatNumber(summary?.compliantEndpoints)}
            icon={<ComputerIcon style={{ fontSize: 18 }} />}
          />
          <EdrKpiCard
            label="Non-Compliant Endpoints"
            value={formatNumber(summary?.nonCompliantEndpoints)}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          />
          <EdrKpiCard
            label="Failed Security Checks"
            value={formatNumber(summary?.failedSecurityChecks)}
            icon={<ReportIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          />
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Controls…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onSort={handleSort}
            resetSortFlag={resetSortFlag}
            onRowClick={(row) => openComplianceDetail(row as unknown as WazuhComplianceControlItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default ConfigurationAssessment;
